import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { Button, ProgressBar } from '../../components/ui';

function TrajectoryChart() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const padding = 40;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw grid lines
    ctx.strokeStyle = '#222222';
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 4; i++) {
      const y = padding + (height - 2 * padding) * (i / 4);
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Vertical grid lines
    for (let i = 0; i <= 12; i++) {
      const x = padding + (width - 2 * padding) * (i / 12);
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }

    // Draw the curve
    const drawCurve = (progress: number) => {
      ctx.clearRect(0, 0, width, height);

      // Redraw grid
      ctx.strokeStyle = '#222222';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const y = padding + (height - 2 * padding) * (i / 4);
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }
      for (let i = 0; i <= 12; i++) {
        const x = padding + (width - 2 * padding) * (i / 12);
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, height - padding);
        ctx.stroke();
      }

      // Calculate curve points
      const points: { x: number; y: number }[] = [];
      const totalPoints = Math.floor(100 * progress);

      for (let i = 0; i <= totalPoints; i++) {
        const t = i / 100;
        const x = padding + (width - 2 * padding) * t;
        // Exponential growth curve
        const value = Math.pow(t, 1.8);
        const y = height - padding - (height - 2 * padding) * value;
        points.push({ x, y });
      }

      if (points.length < 2) return;

      // Draw filled area
      const gradient = ctx.createLinearGradient(0, height - padding, 0, padding);
      gradient.addColorStop(0, 'rgba(212, 175, 55, 0)');
      gradient.addColorStop(1, 'rgba(212, 175, 55, 0.3)');

      ctx.beginPath();
      ctx.moveTo(padding, height - padding);
      points.forEach((point) => ctx.lineTo(point.x, point.y));
      ctx.lineTo(points[points.length - 1].x, height - padding);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw line
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach((point) => ctx.lineTo(point.x, point.y));
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw end point
      if (points.length > 0) {
        const lastPoint = points[points.length - 1];
        ctx.beginPath();
        ctx.arc(lastPoint.x, lastPoint.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#d4af37';
        ctx.fill();
      }
    };

    // Animate the curve
    let progress = 0;
    const animate = () => {
      progress += 0.02;
      if (progress > 1) progress = 1;
      drawCurve(progress);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    // Small delay before starting animation
    setTimeout(animate, 300);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={360}
      height={280}
      className="w-full max-w-lg"
    />
  );
}

export function TrajectoryScreen() {
  const navigate = useNavigate();
  const { nextStep } = useStore();

  const handleStart = () => {
    nextStep();
    navigate('/onboarding/lockin');
  };

  return (
    <div className="screen-container onboarding-screen">
      <ProgressBar current={15} total={17} className="mb-8" />

      <div className="flex-1 flex flex-col">
        <h1 className="text-2xl md:text-3xl font-bold mb-8 text-center">
          You're building the{' '}
          <span className="text-accent">most attractive version</span>
          {' '}of yourself.
        </h1>

        <div className="flex-1 flex items-center justify-center">
          <div className="relative w-full flex items-center justify-center">
            <TrajectoryChart />
            <span className="absolute left-0 top-0 text-xs text-secondary">
              Confidence
            </span>
            <span className="absolute right-0 bottom-0 text-xs text-secondary">
              Looks
            </span>
          </div>
        </div>

        {/* X-axis label */}
        <div className="text-center text-sm text-muted mb-4">
          Weeks 1-12
        </div>
      </div>

      <Button onClick={handleStart}>
        Start my plan
      </Button>
    </div>
  );
}
